(() => {
  const MINIMUM = 20;
  let activePromise = null;
  let summaryPromise = null;
  let summaryCache = null;
  let summaryCacheTime = 0;
  const SUMMARY_CACHE_MS = 750;

  const number = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const isEligible = (item) => item?.properties?._cutMinimumEligible === 'true'
    && number(item?.properties?._cutBasePrice, 0) > 0;

  const baseUnitPrice = (item) => number(item?.properties?._cutBasePrice, 0);

  const summaryFromCart = (cart) => {
    const eligible = (cart?.items || []).filter(isEligible);
    const cutSubtotal = eligible.reduce((sum, item) => {
      return sum + baseUnitPrice(item) * Math.max(1, Number(item.quantity) || 1);
    }, 0);
    return {
      cutSubtotal,
      adjustment: cutSubtotal > 0 && cutSubtotal < MINIMUM ? MINIMUM - cutSubtotal : 0,
      minimum: MINIMUM,
    };
  };

  const cacheSummary = (summary) => {
    summaryCache = summary;
    summaryCacheTime = Date.now();
    return summary;
  };

  const notifySummary = (summary) => {
    try {
      window.dispatchEvent(new CustomEvent('plasticare:cut-minimum-updated', { detail: summary }));
    } catch (_) {}
  };

  const getSummary = async ({ force = false } = {}) => {
    if (!force && summaryCache && (Date.now() - summaryCacheTime) < SUMMARY_CACHE_MS) {
      return summaryCache;
    }
    if (summaryPromise) return summaryPromise;

    summaryPromise = fetchCart()
      .then((cart) => cacheSummary(summaryFromCart(cart)))
      .finally(() => { summaryPromise = null; });

    return summaryPromise;
  };

  const fetchCart = async () => {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to read cart for cut-to-size minimum.');
    return response.json();
  };

  const fetchSections = async (sections, sectionsUrl) => {
    if (!Array.isArray(sections) || !sections.length) return null;
    const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
    url.searchParams.set('sections', sections.join(','));
    if (sectionsUrl) url.searchParams.set('sections_url', sectionsUrl);
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) return null;
    return response.json();
  };

  const sameMoney = (a, b) => Math.abs(number(a) - number(b)) < 0.0001;

  async function rebalanceInternal(options = {}) {
    let cart = options.cart || await fetchCart();
    const eligible = cart.items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => isEligible(item));

    if (!eligible.length) {
      const sections = await fetchSections(options.sections, options.sectionsUrl);
      const summary = cacheSummary({ cutSubtotal: 0, adjustment: 0, minimum: MINIMUM });
      notifySummary(summary);
      return { cart, sections, ...summary };
    }

    const cutSubtotal = eligible.reduce((sum, { item }) => {
      return sum + (baseUnitPrice(item) * Math.max(1, Number(item.quantity) || 1));
    }, 0);
    const adjustment = cutSubtotal > 0 && cutSubtotal < MINIMUM
      ? MINIMUM - cutSubtotal
      : 0;

    const carrier = eligible.find(({ item }) => Number(item.quantity) === 1) || eligible[0];
    const updates = [];

    eligible.forEach(({ item, index }) => {
      const qty = Math.max(1, Number(item.quantity) || 1);
      const base = baseUnitPrice(item);
      const isCarrier = adjustment > 0 && index === carrier.index;
      const targetUnit = isCarrier ? base + (adjustment / qty) : base;
      const props = { ...(item.properties || {}) };

      props._cutMinimumEligible = 'true';
      props._cutBasePrice = base.toFixed(4);
      props._cutMinimumThreshold = MINIMUM.toFixed(2);
      props._totalPrice = targetUnit.toFixed(4);

      if (isCarrier) {
        props._cutMinimumAdjustment = adjustment.toFixed(2);
        props._cutMinimumApplied = 'true';
      } else {
        delete props._cutMinimumAdjustment;
        delete props._cutMinimumApplied;
      }

      // Remove the previous per-line minimum flags. The minimum is cart-wide now.
      delete props._minimumPriceApplied;
      delete props._minimumPrice;
      delete props._preMinimumTotal;

      const currentAdjustment = number(item.properties?._cutMinimumAdjustment, 0);
      const needsUpdate = !sameMoney(item.properties?._totalPrice, targetUnit)
        || !sameMoney(item.properties?._cutBasePrice, base)
        || (isCarrier && !sameMoney(currentAdjustment, adjustment))
        || (!isCarrier && currentAdjustment > 0)
        || item.properties?._minimumPriceApplied;

      if (needsUpdate) updates.push({ line: index + 1, quantity: qty, properties: props });
    });

    let lastResponse = null;
    for (let i = 0; i < updates.length; i += 1) {
      const update = updates[i];
      const isLast = i === updates.length - 1;
      const body = {
        line: update.line,
        quantity: update.quantity,
        properties: update.properties,
      };
      if (isLast && Array.isArray(options.sections) && options.sections.length) {
        body.sections = options.sections;
        body.sections_url = options.sectionsUrl || window.location.pathname;
      }

      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('Unable to apply cut-to-size cart minimum.');
      lastResponse = await response.json();
      cart = lastResponse;
    }

    let sections = lastResponse?.sections || null;
    if (!updates.length || !sections) {
      sections = await fetchSections(options.sections, options.sectionsUrl);
    }

    const summary = cacheSummary({ cutSubtotal, adjustment, minimum: MINIMUM });
    notifySummary(summary);
    return { cart, sections, ...summary };
  }

  window.PlasticareCutMinimum = {
    minimum: MINIMUM,
    rebalance(options = {}) {
      if (activePromise) return activePromise;
      activePromise = rebalanceInternal(options)
        .catch((error) => {
          console.error('[Plasticare cut minimum]', error);
          return null;
        })
        .finally(() => { activePromise = null; });
      return activePromise;
    },
    getSummary(options = {}) {
      return getSummary(options);
    },
    async getPreview(rawLineTotal, options = {}) {
      const normalLineTotal = Math.max(0, number(rawLineTotal, 0));
      const summary = await getSummary(options);
      const combinedBaseTotal = summary.cutSubtotal + normalLineTotal;
      const adjustment = normalLineTotal > 0 && combinedBaseTotal < MINIMUM
        ? MINIMUM - combinedBaseTotal
        : 0;
      return {
        ...summary,
        normalLineTotal,
        combinedBaseTotal,
        lineAdjustment: adjustment,
        displayLineTotal: normalLineTotal + adjustment,
        minimumApplied: adjustment > 0,
      };
    },
  };
})();
